import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import {
	createPreFillableFormTemplate,
	getDealsListData,
	uploadPreFillableFormTemplateFile,
} from '@dealstack/frontend/shared/dataExchange/services/god.service.ts';
import { useForm, UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { useMutation, useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GODMODE_ROOT_ROUTES } from '@dealstack/frontend/admin/pages/Routing/Routing.tsx';
import { fileToBlob } from '@dealstack/frontend/shared/documentPreview.ts';
import { normalizeFileNameToUI } from '@dealstack/shared/lib/format/string.ts';
import { usePreFillableFormOrganizationOptions } from '@dealstack/frontend/admin/pages/Manage/ManagePreFillableForms/common/usePreFillableFormOrganizationOptions.ts';
import {
	CustomDetailCategory,
	PreFillableFormTemplateTypeEnum,
} from '@dealstack/shared/lib/generated';

export enum CREATE_TEMPLATE_FORM_FIELDS {
	NAME = 'name',
	INSTRUCTION = 'instruction',
	TYPE = 'type',
	ASSIGNED_PROJECTS = 'assignedProjects',
	ASSIGNED_ORGANIZATIONS = 'assignedOrganizations',
	FILLABLE_FIELDS_MAPPING = 'fillableFieldsMapping',
	CUSTOM_DETAIL_DEFINITIONS = 'customDetailDefinitions',
	CATEGORY = 'category',
}

const templateFillableFieldShape = z.object({
	fieldKey: z.string(),
	fieldSourceTemplate: z.string().nullish(),
	fieldSourceDescription: z.string().nullish(),
});

export const templateCustomDetailDefinitionShape = z.object({
	localId: z.string().uuid(),
	name: z.string().nonempty(),
	instruction: z.string().optional(),
});

const formShape = z.object({
	[CREATE_TEMPLATE_FORM_FIELDS.NAME]: z.string(),
	[CREATE_TEMPLATE_FORM_FIELDS.INSTRUCTION]: z.string().nullish(),
	[CREATE_TEMPLATE_FORM_FIELDS.TYPE]: z.nativeEnum(PreFillableFormTemplateTypeEnum),
	[CREATE_TEMPLATE_FORM_FIELDS.CATEGORY]: z.nativeEnum(CustomDetailCategory).optional(),
	[CREATE_TEMPLATE_FORM_FIELDS.ASSIGNED_PROJECTS]: z.array(z.string()),
	[CREATE_TEMPLATE_FORM_FIELDS.ASSIGNED_ORGANIZATIONS]: z.array(z.string()),
	[CREATE_TEMPLATE_FORM_FIELDS.FILLABLE_FIELDS_MAPPING]: z.array(templateFillableFieldShape),
	[CREATE_TEMPLATE_FORM_FIELDS.CUSTOM_DETAIL_DEFINITIONS]: z.array(
		templateCustomDetailDefinitionShape
	),
});

type CreatePreFillableFormTemplateFormValues = z.infer<typeof formShape>;
export type CreatePreFillableFormTemplateForm =
	UseFormReturn<CreatePreFillableFormTemplateFormValues>;

export const useCreatePreFillableFormController = () => {
	const navigate = useNavigate();

	const uploadFileRequest = useWrappedRequest(uploadPreFillableFormTemplateFile);
	const getDealsRequest = useQuery({
		queryKey: [API_PATHS.GODMODE_DEALS_GET],
		queryFn: ({ signal }) => getDealsListData(API_PATHS.GODMODE_DEALS_GET, signal),
	});
	const createPreFillableTemplateRequest = useMutation({
		mutationKey: [API_PATHS.GODMODE_PRE_FILLABLE_FORM_TEMPLATE_CREATE_POST],
		mutationFn: createPreFillableFormTemplate,
	});

	const [fileBlob, setFileBlob] = useState<Blob>();
	const uploadFileResponse = uploadFileRequest.response?.payload;

	const createPreFillableFormTemplateForm = useForm<CreatePreFillableFormTemplateFormValues>({
		mode: 'all',
		resolver: zodResolver(formShape),
		defaultValues: {
			[CREATE_TEMPLATE_FORM_FIELDS.ASSIGNED_PROJECTS]: [],
			[CREATE_TEMPLATE_FORM_FIELDS.ASSIGNED_ORGANIZATIONS]: [],
			[CREATE_TEMPLATE_FORM_FIELDS.FILLABLE_FIELDS_MAPPING]: [],
			[CREATE_TEMPLATE_FORM_FIELDS.CUSTOM_DETAIL_DEFINITIONS]: [],
		},
	});

	const onFileDrop = async (file: File) => {
		const fileBlob = await fileToBlob(file);
		setFileBlob(fileBlob);

		uploadFileRequest.fetch(file).onSuccess(({ payload }) => {
			if (!payload) {
				return;
			}
			createPreFillableFormTemplateForm.setValue(
				CREATE_TEMPLATE_FORM_FIELDS.NAME,
				normalizeFileNameToUI(payload.fileName) ?? ''
			);
			createPreFillableFormTemplateForm.setValue(
				CREATE_TEMPLATE_FORM_FIELDS.FILLABLE_FIELDS_MAPPING,
				payload.fillableFieldsKeys.map((fieldKey) => ({ fieldKey }))
			);
		});
	};

	const onSubmit = (formValues: CreatePreFillableFormTemplateFormValues) => {
		if (!uploadFileResponse?.fileId) {
			return;
		}

		createPreFillableTemplateRequest.mutate(
			{
				fileId: uploadFileResponse.fileId,
				name: formValues.name,
				instruction: formValues.instruction ?? '',
				type: formValues.type,
				category: formValues.category,
				assignedProjects: formValues.assignedProjects,
				assignedOrganizations: formValues.assignedOrganizations,
				customDetailsDefinitions: formValues.customDetailDefinitions,
				fillableFieldsMapping: formValues.fillableFieldsMapping.map((field) => ({
					fieldKey: field.fieldKey,
					fieldSourceTemplate: field.fieldSourceTemplate ?? undefined,
					fieldSourceDescription: field.fieldSourceDescription ?? undefined,
				})),
			},
			{
				onSuccess: () => {
					showToastV2({ title: 'Created successfully' });
					navigate(GODMODE_ROOT_ROUTES.MANAGE_PRE_FILLABLE_FORMS);
				},
			}
		);
	};

	const assignedProjectsOptions = useMemo(() => {
		if (!getDealsRequest.data?.payload) {
			return [];
		}
		return getDealsRequest.data.payload.map((deal) => ({ value: deal.id, label: deal.name }));
	}, [getDealsRequest.data?.payload]);

	const assignedOrganizationsOptions = usePreFillableFormOrganizationOptions();

	const [showManageCustomDetailsDefinitionsModal, setShowManageCustomDetailsDefinitionsModal] =
		useState(false);

	return {
		navigate,
		onFileDrop,
		isFileUploading: uploadFileRequest.isLoading,
		showForm: !!uploadFileResponse?.fileId,
		uploadedFileName: uploadFileResponse?.fileName,
		createPreFillableFormTemplateForm,
		assignedProjectsOptions,
		assignedOrganizationsOptions,
		onSubmit,
		isFormSubmitting:
			createPreFillableFormTemplateForm.formState.isSubmitting ||
			createPreFillableTemplateRequest.isPending,
		fileBlob,
		showManageCustomDetailsDefinitionsModal,
		setShowManageCustomDetailsDefinitionsModal,
	};
};
